/* global debounce */
if (!customElements.get('cart-note')) {
  class CartNote extends HTMLElement {
    constructor() {
      super();
      this.disclosure = this.closest('details');

      if (this.disclosure && this.disclosure.matches('.cart-note-disclosure')) {
        this.cartNoteToggle = this.disclosure.querySelector('.js-show-note');
      }

      // Single-flight queue: only one /cart/update note write in flight at a time.
      // If a newer value arrives mid-flight, it's stored in _pendingValue and
      // saved as soon as the current request resolves. The latest value always wins.
      this._inFlight = false;
      this._pendingValue = null;

      // Tracks the last value we successfully sent, so we don't re-POST identical
      // payloads (e.g. when blur fires after the debounced save already saved).
      this._lastSavedValue = null;

      this.init();
    }

    init() {
      // Read the textarea's initial value so we don't fire a redundant first save.
      const initial = this.querySelector('textarea');
      if (initial) this._lastSavedValue = initial.value;

      // Longer debounce (800ms) gives other cart-writing apps room to finish
      // their own writes between keystrokes, reducing collision pressure.
      this.debouncedHandleNoteChange = debounce(this.handleNoteChange.bind(this), 800);
      this.addEventListener('input', this.debouncedHandleNoteChange);

      // Force-save when any close button is clicked (Save button or X button)
      const popup = this.closest('.cart-drawer-popup__content');
      if (popup) {
        popup.querySelectorAll('.js-close').forEach(btn => {
          btn.addEventListener('click', () => this.forceSave());
        });
      }

      // Force-save when textarea loses focus (tap outside)
      const textarea = this.querySelector('textarea');
      if (textarea) {
        textarea.addEventListener('blur', () => this.forceSave());
      }

      // Force-save before the disclosure collapses
      if (this.disclosure) {
        this.disclosure.addEventListener('toggle', () => {
          if (!this.disclosure.open) this.forceSave();
        });
      }

      // Intercept checkout form submission. The form submit navigates the page,
      // which would cancel any pending fetch. We await the save (with retry)
      // before allowing navigation.
      const form = document.getElementById('cart-drawer-form');
      if (form) {
        form.addEventListener('submit', (evt) => {
          const ta = this.querySelector('textarea');
          if (!ta || !ta.value) return; // no note to save, proceed normally

          // If nothing changed since last save, no need to wait.
          if (ta.value === this._lastSavedValue && !this._inFlight) return;

          evt.preventDefault();

          // Wait for any in-flight save AND any new save to fully complete
          // before navigating. keepalive ensures it survives unload regardless.
          this._saveWithRetry(ta.value, { keepalive: true })
            .finally(() => form.submit());
        });
      }

      // sendBeacon backup on page unload — last-resort, fire-and-forget.
      window.addEventListener('beforeunload', () => {
        const ta = this.querySelector('textarea');
        if (ta && ta.value && ta.value !== this._lastSavedValue) {
          navigator.sendBeacon(
            theme.routes.cartUpdate,
            new Blob([JSON.stringify({ note: ta.value })], { type: 'application/json' })
          );
        }
      });
    }

    _updateToggleLabel(value) {
      if (!this.cartNoteToggle) return;
      const label = value ? theme.strings.editCartNote : theme.strings.addCartNote;
      if (this.cartNoteToggle.textContent !== label) {
        this.cartNoteToggle.textContent = label;
      }
    }

    // Single-flight save with retry on 409 Conflict.
    // Returns a Promise so callers (e.g. the form submit handler) can await it.
    async _saveWithRetry(value, opts = {}) {
      // Skip identical-value saves entirely.
      if (value === this._lastSavedValue && !this._inFlight) return;

      // If a save is already in flight, just queue the latest value.
      // The in-flight loop will pick it up.
      if (this._inFlight) {
        this._pendingValue = value;
        return;
      }

      this._inFlight = true;
      let current = value;

      try {
        while (current !== null) {
          this._pendingValue = null;
          // Skip if already saved (could happen if multiple events queued same value)
          if (current !== this._lastSavedValue) {
            const ok = await this._postNote(current, opts);
            if (ok) this._lastSavedValue = current;
          }
          // If a newer value arrived during the request, save that next.
          current = this._pendingValue;
        }
      } finally {
        this._inFlight = false;
      }
    }

    // Returns true on success, false on final failure.
    async _postNote(value, opts = {}) {
      const body = JSON.stringify({ note: value });
      const maxAttempts = 4;
      let delay = 150; // ms

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const res = await fetch(theme.routes.cartUpdate, {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body,
            keepalive: opts.keepalive === true
          });

          if (res.ok) return true;

          // 409 = another cart write is in progress. Wait and retry.
          if (res.status === 409 && attempt < maxAttempts) {
            await new Promise(r => setTimeout(r, delay));
            delay *= 2; // 150 → 300 → 600
            continue;
          }

          return false;
        } catch (err) {
          // Network/abort error — retry once more, then give up.
          if (attempt < maxAttempts) {
            await new Promise(r => setTimeout(r, delay));
            delay *= 2;
            continue;
          }
          return false;
        }
      }
      return false;
    }

    // Immediate save (used by blur, close buttons, disclosure toggle).
    // No-ops if value hasn't changed since last successful save.
    forceSave() {
      const textarea = this.querySelector('textarea');
      if (!textarea) return;
      this._updateToggleLabel(textarea.value);
      return this._saveWithRetry(textarea.value, { keepalive: true });
    }

    // Debounced save (used while typing).
    handleNoteChange(evt) {
      this._updateToggleLabel(evt.target.value);
      this._saveWithRetry(evt.target.value);
    }
  }

  customElements.define('cart-note', CartNote);
}